import { Fragment, ReactNode } from "react";

type BlogContentProps = {
  content: string;
  compact?: boolean;
};

type ContentBlock =
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] };

const parseContent = (content: string): ContentBlock[] => {
  const normalized = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  if (!normalized) return [];

  const lines = normalized.split("\n");
  const blocks: ContentBlock[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];

  const flushParagraph = () => {
    const text = paragraphLines.join(" ").replace(/\s+/g, " ").trim();
    if (text) blocks.push({ type: "paragraph", text });
    paragraphLines = [];
  };

  const flushList = () => {
    if (listItems.length > 0) blocks.push({ type: "list", items: listItems });
    listItems = [];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    if (line.startsWith("### ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h3", text: line.slice(4).trim() });
      return;
    }

    if (line.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h2", text: line.slice(3).trim() });
      return;
    }

    if (line.startsWith("- ")) {
      flushParagraph();
      listItems.push(line.slice(2).trim());
      return;
    }

    flushList();
    paragraphLines.push(line);
  });

  flushParagraph();
  flushList();

  return blocks;
};

const BlogContent = ({ content, compact = false }: BlogContentProps) => {
  const blocks = parseContent(content);

  const renderText = (text: string): ReactNode => {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);

    return parts.map((part, index) => {
      if (/^https?:\/\//.test(part)) {
        return (
          <a
            key={`${part}-${index}`}
            href={part}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-orange-600 underline decoration-orange-200 underline-offset-4 hover:text-orange-700"
          >
            {part}
          </a>
        );
      }

      return <Fragment key={`${part}-${index}`}>{part}</Fragment>;
    });
  };

  return (
    <div className={compact ? "space-y-5" : "space-y-7"}>
      {blocks.map((block, index) => {
        if (block.type === "h2") {
          return (
            <h2
              key={`h2-${index}`}
              className={`${compact ? "text-2xl" : "text-3xl md:text-4xl"} pt-5 font-black leading-tight tracking-tight text-slate-950`}
            >
              {block.text}
            </h2>
          );
        }

        if (block.type === "h3") {
          return (
            <h3
              key={`h3-${index}`}
              className={`${compact ? "text-xl" : "text-2xl md:text-3xl"} pt-3 font-extrabold leading-tight text-slate-900`}
            >
              {block.text}
            </h3>
          );
        }

        if (block.type === "list") {
          return (
            <ul key={`list-${index}`} className="space-y-3 pl-1">
              {block.items.map((item, itemIndex) => (
                <li
                  key={`${item}-${itemIndex}`}
                  className={`${compact ? "text-base" : "text-lg"} flex gap-3 leading-8 text-slate-700`}
                >
                  <span className="mt-3 h-2 w-2 shrink-0 rounded-full bg-orange-500" aria-hidden="true" />
                  <span>{renderText(item)}</span>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p
            key={`paragraph-${index}`}
            className={`${compact ? "text-base leading-7" : "text-lg leading-8 md:text-[1.12rem] md:leading-9"} text-slate-700`}
          >
            {renderText(block.text)}
          </p>
        );
      })}
    </div>
  );
};

export default BlogContent;
